import classNames from 'classnames';
import { ReactElement, useMemo } from 'react';
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams, GridValueGetterParams } from '@enterprise/material-data-grid';
import i18n from '../../../../i18n';
import { ImportRowModel } from '../../../../core/models';
import styles from '../ImportPage.module.scss';
import { ImportStatusCellRenderer } from '../ImportStatusCellRenderer';
import type { ImportGridRow } from './useImportGrid.hook';

export enum ImportGridColumn {
    IdexxSapId = 'displayable.idexxSapId',
    PracticeName = 'displayable.practiceName',
    ItemId = 'item.pimsId',
    Description = 'item.hospitalDescription',
    CurrentSalesPrice = 'displayable.currentSalesPrice',
    NewSalesPrice = 'configurable.salesPrice',
    ClassDescription = 'item.classification.description',
    SubclassDescription = 'item.subClassification.description',
    Status = 'item.status.message',
    SupplierCode = 'item.supplierCode',
    CurrentListPrice = 'displayable.currentListPrice',
    NewListPrice = 'configurable.listPrice',
    CurrentCostPrice = 'displayable.currentCostPrice',
    NewCostPrice = 'configurable.costPrice',
}

type ConfigurablePriceField = 'listPrice' | 'costPrice' | 'salesPrice';

const COLUMN_MIN_WIDTH = 200;
const COLUMN_STATUS_MIN_WIDTH = 350;

const importRowValue =
    (select: (row: ImportRowModel) => unknown) =>
    ({ row }: GridValueGetterParams<ImportGridRow>) =>
        select(row);

const header = (params: GridColumnHeaderParams<ImportGridRow>): ReactElement => (
    <div className={styles.headerContainer}>
        <div className={classNames('spot-typography__text--body', styles.headerCell)}>{params.colDef.headerName}</div>
    </div>
);

const applyColumnDefaults = (columns: GridColDef<ImportGridRow>[]): GridColDef<ImportGridRow>[] =>
    columns.map((column) => ({
        minWidth: COLUMN_MIN_WIDTH,
        renderHeader: header,
        ...column,
    }));

const createCurrentPriceCellRenderer =
    (configurableField: ConfigurablePriceField) =>
    ({ value, row }: GridRenderCellParams<ImportGridRow>) => {
        const configurableValue = row?.configurable?.[configurableField];

        if (configurableValue === undefined || configurableValue === null) {
            return null;
        }

        return value;
    };

export interface ImportGridColumnsDetails {
    columns: GridColDef<ImportGridRow>[];
}

export const useImportGridColumns = (): ImportGridColumnsDetails => {
    const columns = useMemo<GridColDef<ImportGridRow>[]>(
        () =>
            applyColumnDefaults([
                {
                    field: ImportGridColumn.IdexxSapId,
                    headerName: i18n.t('controlCenter:import.column.idexxSapId', 'IDEXX SAP ID') || 'IDEXX SAP ID',
                    valueGetter: importRowValue((row) => row.displayable?.idexxSapId),
                },
                {
                    field: ImportGridColumn.PracticeName,
                    headerName: i18n.t('controlCenter:import.column.practiceName', 'Practice Name') || 'Practice Name',
                    valueGetter: importRowValue((row) => row.displayable?.practiceName),
                },
                {
                    field: ImportGridColumn.ItemId,
                    headerName: i18n.t('controlCenter:import.column.itemId', 'Item ID') || 'Item ID',
                    valueGetter: importRowValue((row) => row.item?.pimsId),
                },
                {
                    field: ImportGridColumn.Description,
                    headerName: i18n.t('controlCenter:import.column.description', 'Description') || 'Description',
                    valueGetter: importRowValue((row) => row.item?.hospitalDescription),
                },
                {
                    field: ImportGridColumn.CurrentSalesPrice,
                    align: 'right',
                    headerName: i18n.t('controlCenter:import.column.currentBasePrice', 'Current Base Price') || 'Current Base Price',
                    headerClassName: styles.originalPriceHeader,
                    valueGetter: importRowValue((row) => row.displayable?.currentSalesPrice),
                    renderCell: createCurrentPriceCellRenderer('salesPrice'),
                },
                {
                    field: ImportGridColumn.NewSalesPrice,
                    align: 'left',
                    headerName: i18n.t('controlCenter:import.column.newBasePrice', 'New Base Price') || 'New Base Price',
                    valueGetter: importRowValue((row) => row.configurable?.salesPrice),
                },
                {
                    field: ImportGridColumn.ClassDescription,
                    headerName: i18n.t('controlCenter:import.column.classDescription', 'Class Description') || 'Class Description',
                    valueGetter: importRowValue((row) => row.item?.classification?.description),
                },
                {
                    field: ImportGridColumn.SubclassDescription,
                    headerName: i18n.t('controlCenter:import.column.subclassDescription', 'Subclass Description') || 'Subclass Description',
                    valueGetter: importRowValue((row) => row.item?.subClassification?.description),
                },
                {
                    field: ImportGridColumn.Status,
                    headerName: i18n.t('controlCenter:import.column.status', 'Status') || 'Status',
                    minWidth: COLUMN_STATUS_MIN_WIDTH,
                    flex: 1,
                    valueGetter: importRowValue((row) => row.item?.status?.message),
                    cellClassName: styles.statusCell,
                    renderCell: ImportStatusCellRenderer,
                },
            ]),
        [],
    );

    return { columns };
};

export { createCurrentPriceCellRenderer, importRowValue, header as importGridHeader };
